import { type ReactNode, createElement, Fragment } from 'react';

/**
 * Renderizador de Markdown mínimo e seguro para o conteúdo de regras e textos
 * legais (confiável, escrito por nós). Suporta cabeçalhos `##`/`###`/`####`,
 * listas com `-`/`*`, listas numeradas `1.`, parágrafos, links `[texto](url)`,
 * e inline `**negrito**` e `` `código` ``. Não interpreta HTML cru — só cria
 * elementos React.
 */

/**
 * Converte texto inline com **negrito**, *itálico*, `código` e `[texto](url)`
 * em nós React. Links `http(s)` abrem em nova aba (`target="_blank"`); demais
 * esquemas (ex.: `mailto:`, âncoras de rota `#/...`) navegam na mesma aba.
 */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // A alternativa de negrito vem ANTES da de itálico, para que `**` ganhe de `*`.
  const pattern = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[2] !== undefined && match[3] !== undefined) {
      const href = match[3];
      const external = /^https?:\/\//i.test(href);
      nodes.push(
        <a
          key={`a${key++}`}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {match[2]}
        </a>,
      );
    } else if (match[4] !== undefined) {
      nodes.push(<strong key={`b${key++}`}>{match[4]}</strong>);
    } else if (match[5] !== undefined) {
      nodes.push(<em key={`i${key++}`}>{match[5]}</em>);
    } else if (match[6] !== undefined) {
      nodes.push(<code key={`c${key++}`}>{match[6]}</code>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

type Block =
  | { kind: 'heading'; level: 3 | 4 | 5; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'p'; text: string };

/** Quebra o markdown em blocos estruturais. */
function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'p', text: paragraph.join(' ') });
      paragraph = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    if (trimmed === '') {
      flushParagraph();
      continue;
    }

    const heading = /^(#{2,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      // ## -> h3, ### -> h4, #### -> h5 (h1/h2 reservados para a página)
      const level = (heading[1]?.length ?? 2) + 1;
      blocks.push({ kind: 'heading', level: level as 3 | 4 | 5, text: heading[2] ?? '' });
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(trimmed);
    if (bullet) {
      flushParagraph();
      const items: string[] = [bullet[1] ?? ''];
      while (i + 1 < lines.length) {
        const next = /^[-*]\s+(.*)$/.exec((lines[i + 1] ?? '').trim());
        if (!next) break;
        items.push(next[1] ?? '');
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }

    const numbered = /^\d+\.\s+(.*)$/.exec(trimmed);
    if (numbered) {
      flushParagraph();
      const items: string[] = [numbered[1] ?? ''];
      while (i + 1 < lines.length) {
        const next = /^\d+\.\s+(.*)$/.exec((lines[i + 1] ?? '').trim());
        if (!next) break;
        items.push(next[1] ?? '');
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }

    paragraph.push(trimmed);
  }
  flushParagraph();
  return blocks;
}

export function Markdown({ source }: { source: string }): React.JSX.Element {
  const blocks = parseBlocks(source);
  return (
    <div className="md">
      {blocks.map((block, idx) => {
        switch (block.kind) {
          case 'heading':
            return createElement(`h${block.level}`, { key: idx }, renderInline(block.text));
          case 'ul':
            return (
              <ul key={idx}>
                {block.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx}>
                {block.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            );
          case 'p':
            return <p key={idx}>{renderInline(block.text)}</p>;
          default:
            return <Fragment key={idx} />;
        }
      })}
    </div>
  );
}
